import React from 'react';
import {
  privacyPolicyContentKR,
  PricingContent,
  ContentItem,
} from './privacyKR';

interface PricingContentRendererProps {
  content: PricingContent;
}

const SubItemRenderer: React.FC<{
  subItem: ContentItem;
  subIndex: number;
}> = ({ subItem, subIndex }) => {
  return (
    <div className='flex flex-row tracking-tighter'>
      {subItem.number && (
        <div className='w-16 text-center text-xs font-medium md:text-sm'>
          {subItem.number}
        </div>
      )}
      <div className='w-full text-xs font-medium md:text-sm'>
        {subItem.content}
        {subItem.table && (
          <table className='w-full mt-2 border-collapse border border-[#4B4B4B]'>
            {subItem.table.headers && (
              <thead>
                <tr>
                  {subItem.table.headers.map((header, hIndex) => (
                    <th
                      key={hIndex}
                      className='border border-[#4B4B4B] px-4 py-2 text-xs font-bold md:text-sm text-left'
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {subItem.table.rows.map((row, rIndex) => (
                <tr key={rIndex}>
                  {row.cells.map((cell, cIndex) => (
                    <td
                      key={cIndex}
                      className='border border-[#4B4B4B] px-4 py-2 text-xs font-medium md:text-sm'
                    >
                      {cell.content}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {subItem.subItems && (
          <div className='flex flex-col gap-2 mt-2 md:gap-2'>
            {subItem.subItems.map((nestedSubItem, nestedIndex) => (
              <SubItemRenderer
                key={nestedIndex}
                subItem={nestedSubItem}
                subIndex={nestedIndex}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PricingContentRenderer: React.FC<PricingContentRendererProps> = ({
  content,
}) => {
  return (
    <>
      {content.sections.map((section, sectionIndex) => (
        <div
          key={sectionIndex}
          className='flex flex-col gap-3 rounded-lg md:gap-4'
        >
          <p className='text-xl font-bold md:text-2xl'>{section.title}</p>
          <div className='flex flex-col gap-4 p-4 rounded-lg md:gap-10 md:p-4'>
            <div className='flex flex-col gap-2 md:gap-5'>
              {section.subsections.map((subsection, subsectionIndex) => (
                <div key={subsectionIndex} className='flex flex-col'>
                  <p className='text-base font-bold md:text-lg mb-2 md:mb-5'>
                    {subsection.title}
                  </p>
                  <div className='flex flex-col gap-2 md:gap-3'>
                    {subsection.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className='flex flex-row tracking-tighter'
                      >
                        {item.number && (
                          <div className='w-16 text-center text-xs font-medium md:text-sm'>
                            {item.number}
                          </div>
                        )}
                        <div className='w-full text-xs font-medium md:text-sm'>
                          {item.content}
                          {item.table && (
                            <table className='w-full mt-4 border-collapse border border-[#4B4B4B]'>
                              {item.table.headers && (
                                <thead>
                                  <tr>
                                    {item.table.headers.map(
                                      (header, hIndex) => (
                                        <th
                                          key={hIndex}
                                          className='border border-[#4B4B4B] px-4 py-2 text-xs font-bold md:text-sm text-left'
                                        >
                                          {header}
                                        </th>
                                      )
                                    )}
                                  </tr>
                                </thead>
                              )}
                              <tbody>
                                {item.table.rows.map((row, rIndex) => (
                                  <tr key={rIndex}>
                                    {row.cells.map((cell, cIndex) => (
                                      <td
                                        key={cIndex}
                                        className='border border-[#4B4B4B] px-4 py-2 text-xs font-medium md:text-sm'
                                      >
                                        {cell.content}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          {item.subItems && (
                            <div className='flex flex-col gap-2 mt-2 md:gap-2'>
                              {item.subItems.map((subItem, subIndex) => (
                                <SubItemRenderer
                                  key={subIndex}
                                  subItem={subItem}
                                  subIndex={subIndex}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

const PrivacyPolicyKr = () => {
  return (
    <div className='bg-[#1C1C1C] w-full h-full flex flex-col gap-6 p-4 px-10 md:px-20 pb-20 text-white font-noto-sans-kr md:gap-8 md:pt-20 md:pb-20 xl:px-40'>
      <div className='flex flex-col items-center gap-2 h-fit'>
        <img
          src='/logo/maintype_3.png'
          alt='logo'
          className='h-4 w-28 md:h-5 md:w-36'
        />
        <p className='text-xl font-semibold h-auto md:text-3xl md:h-14'>
          {privacyPolicyContentKR.title}
        </p>
      </div>
      <PricingContentRenderer content={privacyPolicyContentKR} />
    </div>
  );
};

export default PrivacyPolicyKr;
